import React from 'react';
import { useTranslation } from '../../i18n/LanguageContext';
import { HelpCircle, Home } from 'lucide-react';

const FAQPage = () => {
  const { t } = useTranslation();

  const faqs = [
    {
      category: 'Général',
      questions: [
        {
          q: 'Qu\'est-ce que WorkBiFlow ?',
          a: 'WorkBiFlow est une plateforme SaaS complète de gestion des ressources humaines qui permet de gérer le pointage, les congés, les notes de frais, et bien plus encore.'
        },
        {
          q: 'Comment créer un compte ?',
          a: 'Vous pouvez créer un compte en cliquant sur "Commencer gratuitement" sur la page d\'accueil. Votre demande sera traitée et vous recevrez un email de confirmation.'
        },
        {
          q: 'Quels navigateurs sont supportés ?',
          a: 'WorkBiFlow fonctionne sur tous les navigateurs modernes : Chrome, Firefox, Safari, Edge. Pour une expérience optimale, nous recommandons Chrome ou Firefox.'
        }
      ]
    },
    {
      category: 'Pointage',
      questions: [
        {
          q: 'Comment pointer mon arrivée ?',
          a: 'Cliquez sur le widget "Pointer" sur la page d\'accueil. Activez votre localisation GPS et votre caméra, puis cliquez sur "Arrivée". Le système vérifiera votre position et votre identité avant d\'enregistrer le pointage.'
        },
        {
          q: 'Pourquoi mon pointage est refusé ?',
          a: 'Le pointage peut être refusé si vous êtes hors de la zone autorisée, si la reconnaissance faciale échoue, ou si vous avez déjà pointé récemment. Vérifiez que vous êtes bien sur le lieu de travail autorisé.'
        },
        {
          q: 'Puis-je pointer sans GPS ?',
          a: 'Non, le pointage GPS est obligatoire pour garantir votre présence sur le lieu de travail. En cas de problème technique, contactez votre manager.'
        }
      ]
    },
    {
      category: 'Congés',
      questions: [
        {
          q: 'Comment demander un congé ?',
          a: 'Allez dans "Congés" > "Nouvelle demande", remplissez le formulaire avec les dates de début et de fin, sélectionnez le type de congé, puis soumettez. Votre demande sera envoyée à votre manager pour validation.'
        },
        {
          q: 'Combien de jours de congé ai-je ?',
          a: 'Consultez vos soldes dans "Gestion des Soldes" ou sur la page d\'accueil. Vous verrez le nombre de jours disponibles pour chaque type de congé.'
        },
        {
          q: 'Puis-je annuler une demande de congé ?',
          a: 'Oui, tant que la demande est en attente de validation, vous pouvez l\'annuler depuis la liste de vos demandes. Une fois approuvée, contactez votre manager.'
        }
      ]
    },
    {
      category: 'Notes de frais',
      questions: [
        {
          q: 'Comment déclarer une note de frais ?',
          a: 'Allez dans "Notes de frais" > "Nouvelle note", remplissez le formulaire avec les informations de votre dépense, ajoutez les justificatifs si nécessaire, puis soumettez.'
        },
        {
          q: 'Quels types de justificatifs sont acceptés ?',
          a: 'Vous pouvez ajouter des photos ou des fichiers PDF de vos reçus et factures. Les formats acceptés sont : JPG, PNG, PDF.'
        },
        {
          q: 'Combien de temps faut-il pour être remboursé ?',
          a: 'Le délai de traitement dépend de votre entreprise. Une fois approuvée par votre manager, la note de frais est traitée selon les procédures de votre entreprise.'
        }
      ]
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkSystemFont, "SF Pro Display", sans-serif',
      padding: '120px 20px 40px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <button
        onClick={() => window.location.href = '/'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          color: '#ffffff',
          padding: '10px 16px',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          marginBottom: '32px'
        }}
      >
        <Home size={18} />
        Retour à l'accueil
      </button>

      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        padding: '40px',
        marginBottom: '32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
          <HelpCircle size={40} color="#00d4ff" />
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 800, margin: 0 }}>
            FAQ - Questions fréquentes
          </h1>
        </div>

        <p style={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, marginBottom: '40px' }}>
          Trouvez rapidement les réponses aux questions les plus fréquentes sur WorkBiFlow.
        </p>

        {faqs.map((category, catIndex) => (
          <div key={catIndex} style={{ marginBottom: '48px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 700,
              marginBottom: '24px',
              color: '#00d4ff',
              borderBottom: '2px solid rgba(0, 212, 255, 0.3)',
              paddingBottom: '12px'
            }}>
              {category.category}
            </h2>
            {category.questions.map((faq, qIndex) => (
              <div
                key={qIndex}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '16px'
                }}
              >
                <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px', color: '#ffffff' }}>
                  {faq.q}
                </h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        ))}

        <div style={{
          marginTop: '48px',
          padding: '32px',
          background: 'rgba(0, 212, 255, 0.1)',
          border: '1px solid rgba(0, 212, 255, 0.3)',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '16px' }}>
            Vous ne trouvez pas la réponse ?
          </h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '24px' }}>
            Notre équipe de support est disponible 24/7 pour répondre à vos questions.
          </p>
          <button
            onClick={() => window.location.href = '/contact'}
            style={{
              background: 'linear-gradient(135deg, #00d4ff, #00a8cc)',
              color: '#ffffff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Contacter le support
          </button>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;

